// App updates (contracts/app-updates.md; config writes admin, the numbers every role): the app.ios config (auto
// mode, the latest and minimum versions with a warning before raising the minimum, the App Store link, the prompt
// cadence), the release notes per version in 13 languages with Translate and phone previews of the update sheet,
// "What's new" and the required update, installs per app version (GET /app/versions) with the last App Store
// lookup, and the Ratings card: rating.prompt and the prompts requested per week by moment with the "Rate" taps
// (GET /ratings).

import { useState } from "react";
import { type AppVersions, useAppVersions, useRatingStats } from "../api/appUpdates";
import { type Locale, LOCALE_LABEL, LOCALES, RTL_LOCALES, TRANSLATE_TARGETS, useTranslate } from "../api/messaging";
import { useConfig, useSetConfig } from "../api/queries";
import { useAuth, useCan } from "../auth/AuthContext";
import { BarChart, ColumnChart } from "../components/charts/charts";
import { tableStyles } from "../components/table";
import { Button, Card, Chip, ConfirmDialog, EmptyState, ErrorState, Field, Loading, PageHeader, Segmented, Select, Stat, TextInput } from "../components/ui";
import {
  AppIos,
  belowMinimum,
  compareVersions,
  MOMENT_LABEL,
  MOMENTS,
  NOTE_LIMITS,
  NOTE_LINKS,
  type NoteDraft,
  noteDraftFrom,
  noteDraftOut,
  noteProblems,
  readAppConfig,
  RatingPrompt,
  VERSION_RE,
} from "../lib/appUpdates";
import { formatDateTime } from "../lib/dates";
import { fmtInt, fmtPct } from "../lib/format";
import { isHttpsUrl } from "../lib/messaging";
import { LocaleTabs, SwitchRow, TranslateError } from "./messaging/components";
import m from "./messaging/messaging.module.css";
import a from "./appupdates.module.css";
import styles from "./pages.module.css";

const same = (x: unknown, y: unknown) => JSON.stringify(x) === JSON.stringify(y);

export function AppUpdatesPage() {
  const canEdit = useCan("app.updates");
  const { admin } = useAuth();
  const config = useConfig();
  const versions = useAppVersions();
  return (
    <>
      <PageHeader
        title="App updates"
        meta={
          canEdit ? (
            <span>The iOS versions players are asked to update to, what's new in each, and when the app asks for a rating.</span>
          ) : (
            <span className={styles.readOnly} data-testid="read-only">
              Read-only · app updates need the admin role (you are {admin?.role})
            </span>
          )
        }
      />
      {config.isPending ? (
        <Card>
          <Loading rows={8} />
        </Card>
      ) : config.isError ? (
        <ErrorState error={config.error} onRetry={() => void config.refetch()} />
      ) : (
        <Page saved={readAppConfig(config.data.values)} canEdit={canEdit} versions={versions.data?.items ?? null} versionsState={versions} />
      )}
    </>
  );
}

type Saved = ReturnType<typeof readAppConfig>;
type VersionRow = AppVersions["items"][number];

function Page({ saved, canEdit, versions, versionsState }: { saved: Saved; canEdit: boolean; versions: VersionRow[] | null; versionsState: ReturnType<typeof useAppVersions> }) {
  const { notes, ...versionFields } = saved.ios;
  return (
    <div className={a.grid}>
      <VersionsCard key={JSON.stringify(versionFields)} saved={saved.ios} canEdit={canEdit} versions={versions} />
      <SharesCard ios={saved.ios} state={versionsState} />
      <NotesCard key={JSON.stringify(notes)} saved={saved.ios} canEdit={canEdit} />
      <RatingsCard key={JSON.stringify(saved.rating)} saved={saved.rating} canEdit={canEdit} />
    </div>
  );
}

// ---------------------------------------------------------------- versions

function VersionsCard({ saved, canEdit, versions }: { saved: AppIos; canEdit: boolean; versions: VersionRow[] | null }) {
  const set = useSetConfig();
  const [auto, setAuto] = useState(saved.auto);
  const [latest, setLatest] = useState(saved.latestVersion);
  const [minimum, setMinimum] = useState(saved.minimumVersion);
  const [storeUrl, setStoreUrl] = useState(saved.storeUrl);
  const [every, setEvery] = useState(String(saved.promptEveryDays));
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");

  const value = { ...saved, auto, latestVersion: latest.trim(), minimumVersion: minimum.trim(), storeUrl: storeUrl.trim(), promptEveryDays: Number(every) };
  const parsed = AppIos.safeParse(value);
  const problem = !VERSION_RE.test(value.latestVersion)
    ? "The latest version looks like 1.1.0."
    : !VERSION_RE.test(value.minimumVersion)
      ? "The minimum version looks like 1.0.0."
      : compareVersions(value.minimumVersion, value.latestVersion) > 0
        ? "The minimum can't be above the latest version."
        : !isHttpsUrl(value.storeUrl)
          ? "The App Store link is an https:// URL."
          : !Number.isInteger(value.promptEveryDays) || value.promptEveryDays < 1 || value.promptEveryDays > 90
            ? "Ask again every 1 to 90 days."
            : !parsed.success
              ? (parsed.error.issues[0]?.message ?? "Check the values.")
              : null;
  const raising = VERSION_RE.test(value.minimumVersion) && compareVersions(value.minimumVersion, saved.minimumVersion) > 0;
  const locked = versions ? belowMinimum(versions, value.minimumVersion) : null;
  const changes: [string, string, string][] = [];
  if (auto !== saved.auto) changes.push(["Auto", saved.auto ? "on" : "off", auto ? "on" : "off"]);
  if (value.latestVersion !== saved.latestVersion) changes.push(["Latest", saved.latestVersion, value.latestVersion]);
  if (value.minimumVersion !== saved.minimumVersion) changes.push(["Minimum", saved.minimumVersion, value.minimumVersion]);
  if (value.storeUrl !== saved.storeUrl) changes.push(["App Store link", saved.storeUrl, value.storeUrl]);
  if (value.promptEveryDays !== saved.promptEveryDays) changes.push(["Ask again every", `${saved.promptEveryDays} d`, `${value.promptEveryDays} d`]);

  const warning = raising ? (
    <div className={m.notice} data-tone="danger" data-testid="minimum-warning">
      <strong>
        Raising the minimum to {value.minimumVersion} locks out every player on an older version until they update
        {locked && locked.devices ? `: about ${fmtInt(locked.devices)} installs (${fmtPct(locked.share)}) used in the last 30 days, on ${locked.versions.join(", ")}` : ""}.
      </strong>
      <span>They see a full-screen “Update IQ Daily” and nothing else works except Help & support. Use it only for breaking changes.</span>
    </div>
  ) : null;

  return (
    <Card title="Versions" actions={<span className="mono">app.ios</span>}>
      <fieldset className={a.body} disabled={!canEdit} data-testid="versions">
        <SwitchRow
          title="Follow the App Store"
          hint="Auto: every hour the server reads the App Store's version and raises the latest to it, so nobody is prompted before it's live."
          checked={auto}
          onChange={setAuto}
          label="Follow the App Store"
          testId="app-auto"
        />
        <div className={a.inline}>
          <Field label="Latest version" hint={auto ? "Kept in step with the App Store; set it by hand only to correct it." : "Players below it get the update sheet."}>
            <TextInput value={latest} onChange={(e) => setLatest(e.target.value)} placeholder="1.1.0" data-testid="app-latest" />
          </Field>
          <Field label="Minimum version" hint="Below it the app can't be used until it updates.">
            <TextInput value={minimum} onChange={(e) => setMinimum(e.target.value)} placeholder="1.0.0" data-testid="app-minimum" />
          </Field>
          <Field label="Ask again every (days)" hint="After the first prompt for a version.">
            <TextInput type="number" min={1} max={90} value={every} onChange={(e) => setEvery(e.target.value)} data-testid="app-every" />
          </Field>
        </div>
        <Field label="App Store link">
          <TextInput value={storeUrl} onChange={(e) => setStoreUrl(e.target.value)} data-testid="app-store-url" />
        </Field>
        {warning}
        {canEdit ? (
          <div className={a.saveRow}>
            <Button
              variant={raising ? "danger" : "primary"}
              disabled={problem !== null || changes.length === 0}
              onClick={() => {
                setTyped("");
                setConfirming(true);
              }}
              data-testid="versions-save"
            >
              Save…
            </Button>
            {problem ? (
              <span className={m.required} data-testid="versions-problem">
                {problem}
              </span>
            ) : changes.length ? (
              <span className={styles.faint}>unsaved changes</span>
            ) : null}
          </div>
        ) : null}
      </fieldset>
      <ConfirmDialog
        open={confirming}
        title={raising ? `Raise the minimum to ${value.minimumVersion}?` : "Save app.ios?"}
        confirmLabel={raising ? "Raise the minimum" : "Save"}
        tone={raising ? "danger" : "primary"}
        confirmDisabled={raising && typed.trim() !== value.minimumVersion}
        busy={set.isPending}
        error={set.error}
        onCancel={() => setConfirming(false)}
        onConfirm={() => parsed.success && set.mutate({ key: "app.ios", value: parsed.data as never }, { onSuccess: () => setConfirming(false) })}
      >
        <ul className={a.changes} data-testid="versions-changes">
          {changes.map(([k, from, to]) => (
            <li key={k}>
              <span>{k}</span>
              <span className="mono">
                {from} → {to}
              </span>
            </li>
          ))}
        </ul>
        {raising ? (
          <>
            {warning}
            <Field label={`Type ${value.minimumVersion} to confirm`}>
              <TextInput value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="minimum-confirm" />
            </Field>
          </>
        ) : (
          <p className={styles.footNote}>Players get it with their next config fetch.</p>
        )}
      </ConfirmDialog>
    </Card>
  );
}

// ---------------------------------------------------------------- share per version

function SharesCard({ ios, state }: { ios: AppIos; state: ReturnType<typeof useAppVersions> }) {
  const items = state.data?.items ?? [];
  const tag = (v: string | null) =>
    v === null
      ? compareVersions(ios.minimumVersion, "1.1.0") >= 0
        ? " · below minimum"
        : " · can't be prompted"
      : !VERSION_RE.test(v)
        ? ""
        : compareVersions(v, ios.minimumVersion) < 0
          ? " · below minimum"
          : compareVersions(v, ios.latestVersion) < 0
            ? " · gets the update sheet"
            : compareVersions(v, ios.latestVersion) === 0
              ? " · latest"
              : " · ahead";
  const d = state.data;
  return (
    <Card title="Installs by version" actions={d ? <span className="mono">{fmtInt(d.total.devices)} installs · 30 d</span> : null}>
      {state.isPending ? (
        <Loading rows={4} />
      ) : state.isError ? (
        <ErrorState error={state.error} onRetry={() => void state.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title="No devices yet">App versions show here once players open the app.</EmptyState>
      ) : (
        <div data-testid="version-shares">
          <BarChart
            rows={items.map((v) => ({ key: v.version ?? "none", label: `${v.version ?? "before 1.1"}${tag(v.version)}`, value: v.share, note: `${fmtInt(v.devices)} installs · ${fmtInt(v.players)} players` }))}
            label="Share of installs by app version"
            valueName="of installs"
            tone="accent"
            format={(v) => fmtPct(v)}
            labelWidth={200}
            integerTicks={false}
          />
          <dl className={a.versionFacts} data-testid="version-facts">
            <div>
              <dt>On {d!.latestVersion} or later</dt>
              <dd>{d!.onLatest.share === null ? "—" : fmtPct(d!.onLatest.share)}</dd>
            </div>
            <div>
              <dt>Below the minimum ({d!.minimumVersion})</dt>
              <dd>{d!.belowMinimum.share === null ? "—" : `${fmtPct(d!.belowMinimum.share)} · ${fmtInt(d!.belowMinimum.devices)}`}</dd>
            </div>
            <div>
              <dt>Last App Store lookup</dt>
              <dd data-testid="last-check">{d!.lastCheck ? `${formatDateTime(d!.lastCheck.checkedAt)} · ${d!.lastCheck.storeVersion ?? "no version"} · ${d!.lastCheck.outcome}` : "never (auto off, or not run yet)"}</dd>
            </div>
          </dl>
          <p className={styles.footNote}>
            Installs whose device was seen since {formatDateTime(d!.since)} ({fmtInt(d!.total.players)} players). Apps before 1.1 report no version and can't be prompted: tell them with a push.
          </p>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- release notes

type PreviewKind = "update" | "whatsNew" | "required";

function NotesCard({ saved, canEdit }: { saved: AppIos; canEdit: boolean }) {
  const set = useSetConfig();
  const translate = useTranslate();
  const canTranslate = useCan("content.translate");
  const [drafts, setDrafts] = useState<Record<string, NoteDraft>>(() => Object.fromEntries(Object.entries(saved.notes).map(([v, e]) => [v, noteDraftFrom(e)])));
  const versionList = Object.keys(drafts).sort((x, y) => compareVersions(y, x));
  const [version, setVersion] = useState(() => versionList.find((v) => v === saved.latestVersion) ?? versionList[0] ?? saved.latestVersion);
  const [locale, setLocale] = useState<Locale>("en");
  const [adding, setAdding] = useState("");
  const [preview, setPreview] = useState<PreviewKind>("update");
  const [confirming, setConfirming] = useState(false);
  const draft = drafts[version];

  const value = { ...saved, notes: Object.fromEntries(Object.entries(drafts).map(([v, d]) => [v, noteDraftOut(d)])) };
  const problems = Object.entries(drafts).flatMap(([v, d]) => noteProblems(d).map((x) => `${v}: ${x}`));
  const dirty = !same(value.notes, Object.fromEntries(Object.entries(saved.notes).map(([v, e]) => [v, noteDraftOut(noteDraftFrom(e))])));

  const edit = (fn: (d: NoteDraft) => NoteDraft) => setDrafts((all) => (all[version] ? { ...all, [version]: fn(all[version]!) } : all));
  const setText = (patch: Partial<NoteDraft["text"]["en"]>) => edit((d) => ({ ...d, text: { ...d.text, [locale]: { ...d.text[locale], ...patch } } }));
  const stateOf = (l: Locale) => {
    if (!draft) return "empty" as const;
    const t = draft.text[l];
    const filled = [t.title, ...t.items].filter((x) => x.trim()).length;
    const want = [draft.text.en.title, ...draft.text.en.items].filter((x) => x.trim()).length;
    return filled === 0 ? ("empty" as const) : filled >= want ? ("complete" as const) : ("partial" as const);
  };
  const addVersion = () => {
    const v = adding.trim();
    if (!VERSION_RE.test(v) || drafts[v]) return;
    setDrafts({ ...drafts, [v]: noteDraftFrom(undefined) });
    setVersion(v);
    setAdding("");
  };
  const runTranslate = () => {
    if (!draft) return;
    const en: Record<string, string> = { title: draft.text.en.title.trim() };
    draft.text.en.items.forEach((t, i) => t.trim() && (en[`items.${i}`] = t.trim()));
    const limits = Object.fromEntries(Object.keys(en).map((k) => [k, k === "title" ? NOTE_LIMITS.title : NOTE_LIMITS.item]));
    translate.mutate(
      { content: { en }, limits },
      {
        onSuccess: (r) =>
          edit((d) => {
            const text = { ...d.text };
            for (const l of TRANSLATE_TARGETS) {
              const got = r.content[l];
              if (!got) continue;
              text[l] = { title: got.title ?? text[l].title, items: d.links.map((_, i) => got[`items.${i}`] ?? text[l].items[i] ?? "") };
            }
            return { ...d, text };
          }),
      },
    );
  };

  const t = draft?.text[locale];
  const rtl = RTL_LOCALES.has(locale);
  return (
    <Card title="Release notes" actions={<span className="mono">app.ios · notes</span>} className={a.wide}>
      <div className={a.notesGrid}>
        <fieldset className={a.body} disabled={!canEdit} data-testid="notes">
          <div className={a.inline}>
            <Field label="Version">
              <Select value={version} onChange={(e) => setVersion(e.target.value)} data-testid="notes-version">
                {versionList.map((v) => (
                  <option key={v} value={v}>
                    {v}
                    {v === saved.latestVersion ? " · latest" : ""}
                  </option>
                ))}
                {!versionList.includes(version) ? <option value={version}>{version}</option> : null}
              </Select>
            </Field>
            <Field label="Notes for another version">
              <span className={a.addRow}>
                <TextInput value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="1.2.0" style={{ width: 100 }} data-testid="notes-add-version" />
                <Button variant="secondary" size="sm" disabled={!VERSION_RE.test(adding.trim()) || Boolean(drafts[adding.trim()])} onClick={addVersion}>
                  Add
                </Button>
              </span>
            </Field>
          </div>

          {!draft ? (
            <EmptyState
              title={`No notes for ${version}`}
              action={
                canEdit ? (
                  <Button variant="secondary" onClick={() => setDrafts({ ...drafts, [version]: noteDraftFrom(undefined) })}>
                    Write notes for {version}
                  </Button>
                ) : null
              }
            >
              The app shows its bundled notes instead.
            </EmptyState>
          ) : (
            <>
              <div className={m.localeBar}>
                <LocaleTabs locale={locale} onLocale={setLocale} stateOf={stateOf} />
                <div className={m.translateRow}>
                  {canTranslate ? (
                    <Button size="sm" variant="secondary" busy={translate.isPending} disabled={!draft.text.en.title.trim()} onClick={runTranslate} data-testid="notes-translate">
                      Translate to all languages
                    </Button>
                  ) : null}
                  <span className={m.translateMeta}>
                    {TRANSLATE_TARGETS.filter((l) => stateOf(l) !== "empty").length} of {TRANSLATE_TARGETS.length} translated · empty languages fall back to English
                  </span>
                </div>
              </div>
              <TranslateError error={translate.error} />
              <Field label={`Title · ${LOCALE_LABEL[locale]}`} hint={`${t!.title.length}/${NOTE_LIMITS.title}`}>
                <TextInput
                  value={t!.title}
                  maxLength={NOTE_LIMITS.title}
                  dir={rtl ? "rtl" : undefined}
                  placeholder={locale === "en" ? "What's new in IQ Daily 1.2" : draft.text.en.title}
                  onChange={(e) => setText({ title: e.target.value })}
                  data-testid="notes-title"
                />
              </Field>
              <ol className={a.items}>
                {draft.links.map((link, i) => (
                  <li key={i} data-testid="notes-item">
                    <TextInput
                      value={t!.items[i] ?? ""}
                      maxLength={NOTE_LIMITS.item}
                      dir={rtl ? "rtl" : undefined}
                      placeholder={locale === "en" ? "One thing that's new, in a sentence" : draft.text.en.items[i]}
                      onChange={(e) => setText({ items: t!.items.map((x, j) => (j === i ? e.target.value : x)) })}
                      aria-label={`Item ${i + 1}`}
                    />
                    <Select
                      value={link ?? ""}
                      disabled={locale !== "en"}
                      title={locale !== "en" ? "Links are set on English and shared by every language" : undefined}
                      onChange={(e) => edit((d) => ({ ...d, links: d.links.map((x, j) => (j === i ? e.target.value || null : x)) }))}
                      aria-label={`Item ${i + 1} link`}
                    >
                      <option value="">No “Try it”</option>
                      {NOTE_LINKS.map((x) => (
                        <option key={x} value={x}>
                          {x}
                        </option>
                      ))}
                    </Select>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={locale !== "en"}
                      onClick={() =>
                        edit((d) => ({
                          links: d.links.filter((_, j) => j !== i),
                          text: Object.fromEntries(LOCALES.map((l) => [l, { ...d.text[l], items: d.text[l].items.filter((_, j) => j !== i) }])) as NoteDraft["text"],
                        }))
                      }
                      aria-label={`Remove item ${i + 1}`}
                    >
                      ×
                    </Button>
                  </li>
                ))}
              </ol>
              <div className={a.addRow}>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={locale !== "en" || draft.links.length >= NOTE_LIMITS.items}
                  onClick={() =>
                    edit((d) => ({
                      links: [...d.links, null],
                      text: Object.fromEntries(LOCALES.map((l) => [l, { ...d.text[l], items: [...d.text[l].items, ""] }])) as NoteDraft["text"],
                    }))
                  }
                  data-testid="notes-add-item"
                >
                  + Item
                </Button>
                <span className={styles.faint}>
                  {draft.links.length} of {NOTE_LIMITS.items} · ≤ {NOTE_LIMITS.item} characters each
                </span>
                <span className={styles.spacer} />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const rest = { ...drafts };
                    delete rest[version];
                    setDrafts(rest);
                    setVersion(Object.keys(rest).sort((x, y) => compareVersions(y, x))[0] ?? saved.latestVersion);
                  }}
                >
                  Remove {version} notes
                </Button>
              </div>
            </>
          )}
          {problems.length ? (
            <ul className={a.problems} data-testid="notes-problems">
              {problems.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          ) : null}
          {canEdit ? (
            <div className={a.saveRow}>
              <Button variant="primary" disabled={!dirty || problems.length > 0} onClick={() => setConfirming(true)} data-testid="notes-save">
                Save notes…
              </Button>
              {dirty ? <span className={styles.faint}>unsaved changes</span> : null}
            </div>
          ) : null}
        </fieldset>

        <div>
          <div className={a.previewHead}>
            <Segmented
              label="Preview"
              value={preview}
              onChange={setPreview}
              options={[
                { value: "update", label: "Update" },
                { value: "whatsNew", label: "What's new" },
                { value: "required", label: "Required" },
              ]}
            />
          </div>
          <NotesPreview kind={preview} version={version} draft={draft ?? null} locale={locale} />
        </div>
      </div>
      <ConfirmDialog
        open={confirming}
        title="Save the release notes?"
        confirmLabel="Save"
        busy={set.isPending}
        error={set.error}
        onCancel={() => setConfirming(false)}
        onConfirm={() => set.mutate({ key: "app.ios", value: value as never }, { onSuccess: () => setConfirming(false) })}
      >
        <p className={styles.prose}>
          Writes the notes of app.ios ({Object.keys(value.notes).join(", ") || "none"}). Players get the latest version's notes, in their language, with their next config fetch.
        </p>
      </ConfirmDialog>
    </Card>
  );
}

/** The phone sheets the notes appear in, in one language with English where it's empty. */
function NotesPreview({ kind, version, draft, locale }: { kind: PreviewKind; version: string; draft: NoteDraft | null; locale: Locale }) {
  const title = draft ? draft.text[locale].title.trim() || draft.text.en.title.trim() : "";
  const items = draft ? draft.links.map((link, i) => ({ text: draft.text[locale].items[i]?.trim() || draft.text.en.items[i]?.trim() || "", link })).filter((x) => x.text) : [];
  const rtl = RTL_LOCALES.has(locale);
  return (
    <div className={a.phone} data-kind={kind} dir={rtl ? "rtl" : undefined} lang={locale} data-testid="notes-preview">
      {kind === "required" ? (
        <div className={a.required}>
          <span className={a.appIcon} aria-hidden />
          <strong>Update IQ Daily</strong>
          <span>This version can't be used any more. Update to keep playing; your progress is saved.</span>
          <span className={a.cta}>Update</span>
          <span className={a.link}>Help & support</span>
        </div>
      ) : (
        <div className={a.sheet}>
          <span className={a.grabber} aria-hidden />
          <span className="monoLabel">{kind === "update" ? `IQ Daily ${version} is here` : "What's new"}</span>
          <strong>{title || "Title"}</strong>
          <ul>
            {items.length ? (
              items.map((x, i) => (
                <li key={i}>
                  <span>{x.text}</span>
                  {kind === "whatsNew" && x.link ? <span className={a.try}>Try it</span> : null}
                </li>
              ))
            ) : (
              <li className={styles.faint}>No items yet.</li>
            )}
          </ul>
          {kind === "update" ? (
            <>
              <span className={a.cta}>Update</span>
              <span className={a.link}>Later</span>
            </>
          ) : (
            <span className={a.cta}>Continue</span>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- ratings

function RatingsCard({ saved, canEdit }: { saved: RatingPrompt; canEdit: boolean }) {
  const set = useSetConfig();
  const stats = useRatingStats();
  const [v, setV] = useState({ ...saved, minActiveDays: String(saved.minActiveDays), minSessions: String(saved.minSessions), cooldownDays: String(saved.cooldownDays) });
  const [confirming, setConfirming] = useState(false);
  const value = { ...v, minActiveDays: Number(v.minActiveDays), minSessions: Number(v.minSessions), cooldownDays: Number(v.cooldownDays), moments: MOMENTS.filter((x) => v.moments.includes(x)) };
  const parsed = RatingPrompt.safeParse(value);
  const problem = !parsed.success ? "Days and sessions are whole numbers (0–365 days, 0–1000 sessions)." : value.enabled && !value.moments.length ? "Pick at least one moment, or turn the prompt off." : null;
  const dirty = !same(value, { ...saved, moments: MOMENTS.filter((x) => saved.moments.includes(x)) });
  const weeks = stats.data?.weeks ?? [];
  const asked = (w: (typeof weeks)[number]) => w.requested;
  const totalAsked = stats.data?.totals.requested ?? 0;
  const totalTaps = stats.data?.totals.rateTaps ?? 0;
  const byMoment = MOMENTS.map((x) => ({ moment: x, total: stats.data?.totals.byMoment[x] ?? 0, last: weeks[weeks.length - 1]?.byMoment[x] ?? 0 }));
  const num = (k: "minActiveDays" | "minSessions" | "cooldownDays", label: string, hint: string) => (
    <Field label={label} hint={hint}>
      <TextInput type="number" min={0} value={v[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} data-testid={`rating-${k}`} />
    </Field>
  );
  return (
    <Card title="Ratings" actions={<span className="mono">rating.prompt</span>} className={a.wide}>
      <div className={a.ratingsGrid}>
        <fieldset className={a.body} disabled={!canEdit} data-testid="rating">
          <SwitchRow
            title="Ask for a rating"
            hint="StoreKit's system prompt only (requestReview), never a custom gate. Apple decides whether it shows, at most 3 times a year."
            checked={v.enabled}
            onChange={(x) => setV((s) => ({ ...s, enabled: x }))}
            label="Ask for a rating"
            testId="rating-enabled"
          />
          <div className={a.inline}>
            {num("minActiveDays", "After active days", "Not in a player's first days.")}
            {num("minSessions", "After sessions", "Not in their first sessions.")}
            {num("cooldownDays", "Cooldown (days)", "Since the last ask.")}
          </div>
          <SwitchRow
            title="Once per app version"
            hint="Never ask twice for the same version."
            checked={v.perVersion}
            onChange={(x) => setV((s) => ({ ...s, perVersion: x }))}
            label="Once per app version"
            testId="rating-per-version"
          />
          <div>
            <span className="monoLabel">Moments</span>
            <ul className={a.moments}>
              {MOMENTS.map((x) => (
                <li key={x}>
                  <label>
                    <input
                      type="checkbox"
                      checked={v.moments.includes(x)}
                      onChange={(e) => setV((s) => ({ ...s, moments: e.target.checked ? [...s.moments, x] : s.moments.filter((y) => y !== x) }))}
                      data-testid={`moment-${x}`}
                    />
                    {MOMENT_LABEL[x]}
                  </label>
                </li>
              ))}
            </ul>
            <p className={styles.faint}>Never after a wrong answer that session, in a solve, cup, Arena or paywall, or within 14 days of a support message.</p>
          </div>
          {canEdit ? (
            <div className={a.saveRow}>
              <Button variant="primary" disabled={!dirty || problem !== null} onClick={() => setConfirming(true)} data-testid="rating-save">
                Save…
              </Button>
              {problem ? <span className={m.required}>{problem}</span> : dirty ? <span className={styles.faint}>unsaved changes</span> : null}
            </div>
          ) : null}
        </fieldset>

        <div className={a.body} data-testid="rating-stats">
          {stats.isPending ? (
            <Loading rows={5} />
          ) : stats.isError ? (
            <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
          ) : (
            <>
              <div className={styles.statStrip} style={{ marginBottom: 0 }}>
                <Stat label="Prompts requested" value={fmtInt(totalAsked)} sub={`${weeks.length} weeks · ${fmtInt(stats.data?.totals.players ?? 0)} players`} />
                <Stat label="Rate taps" value={fmtInt(totalTaps)} sub="Profile → Rate IQ Daily" />
                <Stat label="Taps / 100 asks" value={totalAsked ? (totalTaps / (totalAsked / 100)).toFixed(1) : "—"} />
              </div>
              <div>
                <span className="monoLabel">Prompts requested per week</span>
                <ColumnChart
                  rows={weeks.map((w) => ({
                    key: w.week,
                    label: w.week.slice(5),
                    value: asked(w),
                    notes: MOMENTS.filter((x) => (w.byMoment[x] ?? 0) > 0).map((x) => `${MOMENT_LABEL[x].split(" (")[0]}: ${fmtInt(w.byMoment[x])}`),
                  }))}
                  label="Rating prompts requested per ISO week"
                  valueName="requested"
                  height={150}
                />
              </div>
              <div>
                <span className="monoLabel">“Rate” taps per week</span>
                <ColumnChart rows={weeks.map((w) => ({ key: w.week, label: w.week.slice(5), value: w.rateTaps }))} label="Rate taps per ISO week" valueName="taps" height={110} />
              </div>
              <div className={tableStyles.wrap}>
                <table className={tableStyles.table} data-testid="moments-table">
                  <thead>
                    <tr>
                      <th>Moment</th>
                      <th data-align="right">{weeks.length} weeks</th>
                      <th>Share</th>
                      <th data-align="right">Last week</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byMoment.map((r) => (
                      <tr key={r.moment}>
                        <td className={a.wrapCell}>
                          {MOMENT_LABEL[r.moment]}
                          {!saved.moments.includes(r.moment) ? (
                            <>
                              {" "}
                              <Chip tone="muted">off</Chip>
                            </>
                          ) : null}
                        </td>
                        <td data-align="right" className={tableStyles.mono}>
                          {fmtInt(r.total)}
                        </td>
                        <td>
                          <span className={styles.meter}>
                            <span style={{ width: `${totalAsked ? Math.round((r.total / totalAsked) * 100) : 0}%` }} />
                          </span>
                        </td>
                        <td data-align="right" className={tableStyles.mono}>
                          {fmtInt(r.last)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className={styles.footNote} style={{ marginTop: 0 }}>
                Apple doesn't say whether the prompt showed or what was rated: these are the app's requests. The App Store rating and reviews live in App Store Connect.
              </p>
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirming}
        title="Save rating.prompt?"
        confirmLabel="Save"
        busy={set.isPending}
        error={set.error}
        onCancel={() => setConfirming(false)}
        onConfirm={() => parsed.success && set.mutate({ key: "rating.prompt", value: parsed.data as never }, { onSuccess: () => setConfirming(false) })}
      >
        <p className={styles.prose}>The app reads it with its next config fetch.</p>
      </ConfirmDialog>
    </Card>
  );
}
